import React, { useEffect, useState } from "react";
import api, { formatINR } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { ClipboardList, TrendingUp, Users, Receipt, Calendar, MapPin } from "lucide-react";

function isoDaysAgo(n) {
    const d = new Date(); d.setDate(d.getDate() - n);
    return d.toISOString().slice(0, 10);
}

export default function AdminDRSAnalytics() {
    const [fromDate, setFromDate] = useState(isoDaysAgo(29));
    const [toDate, setToDate] = useState(new Date().toISOString().slice(0, 10));
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = async (from = fromDate, to = toDate) => {
        setBusy(true);
        try {
            const { data } = await api.get("/admin/drs/analytics", { params: { from_date: from, to_date: to } });
            setData(data);
        } finally { setBusy(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const apply = (e) => { e.preventDefault(); load(); };
    const setRange = (days) => {
        const f = isoDaysAgo(days - 1), t = new Date().toISOString().slice(0, 10);
        setFromDate(f); setToDate(t); load(f, t);
    };

    const maxVisits = Math.max(1, ...(data?.weekly_trend || []).map(w => w.visits || 0));
    const maxOrders = Math.max(1, ...(data?.weekly_trend || []).map(w => w.orders || 0));

    return (
        <div data-testid="drs-analytics-page" className="space-y-6">
            <div>
                <div className="label-eyebrow text-sky-700">Field force</div>
                <h1 className="font-serif text-4xl text-stone-900">DRS analytics</h1>
                <p className="text-sm text-stone-500 mt-1">Daily Reporting Sheet visits aggregated across all MRs, with per-MR conversion to MR-placed orders.</p>
            </div>

            {/* Filters */}
            <form onSubmit={apply} className="flex items-end gap-3 flex-wrap bg-white border border-stone-200 p-4">
                <div>
                    <label className="text-[10px] uppercase tracking-widest text-stone-500">From</label>
                    <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="rounded-none border-stone-300 mt-1" data-testid="drs-from" />
                </div>
                <div>
                    <label className="text-[10px] uppercase tracking-widest text-stone-500">To</label>
                    <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="rounded-none border-stone-300 mt-1" data-testid="drs-to" />
                </div>
                <Button type="submit" disabled={busy} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="drs-apply">{busy ? "Loading…" : "Apply"}</Button>
                <div className="flex gap-1 ml-auto">
                    <button type="button" onClick={() => setRange(7)} className="text-xs px-2 py-1 border border-stone-300 hover:bg-stone-50">7 days</button>
                    <button type="button" onClick={() => setRange(30)} className="text-xs px-2 py-1 border border-stone-300 hover:bg-stone-50">30 days</button>
                    <button type="button" onClick={() => setRange(90)} className="text-xs px-2 py-1 border border-stone-300 hover:bg-stone-50">90 days</button>
                </div>
            </form>

            {data && (
                <>
                    {/* KPI cards */}
                    <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
                        <Kpi label="Visits" value={data.totals.visits} icon={ClipboardList} testId="kpi-visits" />
                        <Kpi label="DRS days" value={data.totals.drs_days} icon={Calendar} testId="kpi-days" />
                        <Kpi label="Active MRs" value={data.totals.active_mrs} icon={Users} testId="kpi-active" />
                        <Kpi label="MR orders" value={data.totals.mr_orders} icon={Receipt} testId="kpi-orders" />
                        <Kpi label="Conversion" value={`${data.totals.conversion_pct}%`} icon={TrendingUp} testId="kpi-conv" />
                    </div>

                    {/* Weekly trend */}
                    {data.weekly_trend.length > 0 && (
                        <div className="bg-white border border-stone-200 p-5" data-testid="drs-weekly-trend">
                            <h2 className="font-serif text-xl text-stone-900 mb-1">Weekly trend</h2>
                            <p className="text-xs text-stone-500 mb-4">Bars compare visits vs MR orders week by week.</p>
                            <div className="grid grid-cols-1 gap-2">
                                {data.weekly_trend.map(w => (
                                    <div key={w.week_start} className="grid grid-cols-12 gap-2 items-center text-xs">
                                        <span className="col-span-2 font-mono text-stone-500">{w.week_start}</span>
                                        <div className="col-span-5">
                                            <div className="bg-sky-100 h-5" style={{ width: `${(w.visits / maxVisits) * 100}%` }} />
                                            <span className="text-[10px] text-sky-700 ml-1">{w.visits} visits</span>
                                        </div>
                                        <div className="col-span-5">
                                            <div className="bg-emerald-100 h-5" style={{ width: `${(w.orders / maxOrders) * 100}%` }} />
                                            <span className="text-[10px] text-emerald-700 ml-1">{w.orders} orders</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Per-MR breakdown */}
                    <div className="bg-white border border-stone-200" data-testid="drs-per-mr">
                        <div className="p-4 border-b border-stone-200">
                            <h2 className="font-serif text-xl text-stone-900">Per-MR breakdown</h2>
                            <p className="text-xs text-stone-500">Sorted by visits this period.</p>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead><tr className="bg-stone-50 border-b border-stone-200">
                                    <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">#</th>
                                    <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">MR</th>
                                    <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Territory</th>
                                    <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Visits</th>
                                    <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Days</th>
                                    <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Avg / day</th>
                                    <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">MR orders</th>
                                    <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Revenue</th>
                                    <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Conv %</th>
                                </tr></thead>
                                <tbody>
                                    {data.per_mr.length === 0 ? (
                                        <tr><td colSpan={9} className="p-8 text-center text-sm text-stone-500">No DRS or MR orders in this period.</td></tr>
                                    ) : data.per_mr.map((m, i) => (
                                        <tr key={m.mr_user_id} className="border-b border-stone-100 hover:bg-sky-50/40" data-testid={`drs-mr-${m.mr_user_id}`}>
                                            <td className="p-3 font-mono text-stone-500">{i + 1}</td>
                                            <td className="p-3 font-medium text-stone-900">{m.mr_name}{!m.active && <span className="ml-1 text-[10px] uppercase tracking-widest text-red-700">(inactive)</span>}</td>
                                            <td className="p-3 text-xs text-stone-600 inline-flex items-center gap-1">{m.territory ? <><MapPin className="w-3 h-3 text-sky-700" />{m.territory}</> : "—"}</td>
                                            <td className="p-3 text-right font-mono text-sky-700 font-semibold">{m.visits}</td>
                                            <td className="p-3 text-right font-mono">{m.days_active}</td>
                                            <td className="p-3 text-right font-mono">{m.avg_visits_per_day}</td>
                                            <td className="p-3 text-right font-mono">{m.orders}</td>
                                            <td className="p-3 text-right font-mono text-emerald-700">{formatINR(m.revenue)}</td>
                                            <td className={`p-3 text-right font-mono font-semibold ${m.conversion_pct >= 50 ? "text-emerald-700" : m.conversion_pct >= 20 ? "text-amber-700" : "text-stone-500"}`}>{m.conversion_pct}%</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Visit types */}
                    {data.visit_types.length > 0 && (
                        <div className="bg-white border border-stone-200 p-5" data-testid="drs-visit-types">
                            <h2 className="font-serif text-xl text-stone-900 mb-1">Visits by type</h2>
                            <p className="text-xs text-stone-500 mb-3">Pulled from the DRS `visit_type` field across all entries in this period.</p>
                            <div className="flex flex-wrap gap-2">
                                {data.visit_types.map(v => (
                                    <span key={v.label} className="text-xs px-3 py-1.5 bg-sky-50 border border-sky-200 text-sky-800">
                                        <strong className="text-sky-900">{v.label}</strong> · {v.count}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

function Kpi({ label, value, icon: Icon, testId }) {
    return (
        <div className="bg-white border border-sky-100 p-4" data-testid={testId}>
            <div className="flex items-center justify-between">
                <div className="label-eyebrow text-stone-500">{label}</div>
                {Icon && <Icon className="w-4 h-4 text-sky-700" />}
            </div>
            <div className="font-serif text-3xl text-slate-900 mt-2">{value}</div>
        </div>
    );
}
